import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IconAlertTriangle } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { SimpleTable } from "../../../../components/common/simple-table";
import type { SimpleTableColumn } from "../../../../components/common/simple-table";
import { Pagination } from "../../../../components/common/pagination";
import { AccentStatCard } from "../../../../components/common/accent-stat-card";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { EmptyState } from "../../../../components/common/empty-state";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { DisputeStatusChip } from "../components/dispute-status-chip";
import { DISPUTE_REASON_LABEL } from "../components/dispute-labels";
import {
  useAdminOrderDisputes,
  useAdminOrderDisputeStats,
  type OrderDisputeListDto,
} from "../../hooks/useAdminOrderDisputes";

const PAGE_SIZE = 14;

type StatusFilter =
  | "ALL"
  | "OPEN"
  | "UNDER_REVIEW"
  | "RESOLVED_REFUND"
  | "RESOLVED_REPLACEMENT"
  | "RESOLVED_OTHER"
  | "REJECTED";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "ALL", label: "All" },
  { value: "OPEN", label: "Open" },
  { value: "UNDER_REVIEW", label: "Under Review" },
  { value: "RESOLVED_REFUND", label: "Refund" },
  { value: "RESOLVED_REPLACEMENT", label: "Replacement" },
  { value: "RESOLVED_OTHER", label: "Other" },
  { value: "REJECTED", label: "Rejected" },
];

const RESOLVED_STATUSES = ["RESOLVED_REFUND", "RESOLVED_REPLACEMENT", "RESOLVED_OTHER"];

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

export function AdminOrderDisputesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);

  const rawStatus = searchParams.get("status") as StatusFilter | null;
  const statusFilter: StatusFilter = rawStatus ?? "ALL";

  const disputesQuery = {
    page,
    pageSize: PAGE_SIZE,
    ...(statusFilter !== "ALL" ? { status: statusFilter } : {}),
  };

  const { data, isLoading } = useAdminOrderDisputes(disputesQuery);
  const stats = useAdminOrderDisputeStats();

  const rows: OrderDisputeListDto[] = data?.data ?? [];
  const pageCount = data?.meta?.pageCount ?? 1;
  const totalCount = stats.data?.total ?? 0;
  const byStatus = (stats.data?.byStatus ?? {}) as Record<string, number>;
  const openCount = (byStatus["OPEN"] ?? 0) + (byStatus["UNDER_REVIEW"] ?? 0);
  const resolvedCount = RESOLVED_STATUSES.reduce((sum, s) => sum + (byStatus[s] ?? 0), 0);

  function handleStatusChange(v: StatusFilter) {
    setPage(1);
    const p = new URLSearchParams(searchParams);
    if (v === "ALL") p.delete("status");
    else p.set("status", v);
    setSearchParams(p, { replace: true });
  }

  const columns: SimpleTableColumn<OrderDisputeListDto>[] = [
    {
      key: "id",
      label: "Dispute ID",
      className: "flex-[1.5]",
      render: (r) => (
        <Text variant="bodySm" tone="muted" className="font-mono">
          {r.id.slice(0, 8)}…
        </Text>
      ),
    },
    {
      key: "reason",
      label: "Reason",
      className: "flex-1",
      render: (r) => (
        <Text variant="body" tone="dark">
          {DISPUTE_REASON_LABEL[r.reason] ?? r.reason}
        </Text>
      ),
    },
    {
      key: "saleOrderId",
      label: "Sale Order",
      className: "flex-1",
      render: (r) => (
        <Text variant="body" tone="body">
          #{r.saleOrderId}
        </Text>
      ),
    },
    {
      key: "createdAt",
      label: "Filed",
      className: "flex-[1.2]",
      render: (r) => (
        <Text variant="body" tone="body">
          {formatDate(r.createdAt)}
        </Text>
      ),
    },
    {
      key: "status",
      label: "Status",
      className: "flex-[1.4] flex justify-start",
      render: (r) => <DisputeStatusChip status={r.status} />,
    },
  ];

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-4 py-6 md:px-8 md:py-8">
      <Heading variant="h2" tone="dark">
        Order Disputes
      </Heading>

      <div className="flex gap-4 max-md:gap-2 max-md:[&>*]:min-w-0 max-md:[&>*]:px-3">
        <AccentStatCard bare label="Total disputes" value={String(totalCount)} className="flex-1" />
        <AccentStatCard bare label="Open" value={String(openCount)} className="flex-1" />
        <AccentStatCard bare label="Resolved" value={String(resolvedCount)} className="flex-1" />
      </div>

      <div className="flex items-center gap-4">
        <SegmentedControl
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={handleStatusChange}
        />
      </div>

      {isLoading ? (
        <ShimmerLoader message="Fetching order disputes…" lines={6} />
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite">
          <EmptyState
            icon={IconAlertTriangle}
            title="No order disputes"
            description="Disputes filed by buyers will appear here."
          />
        </div>
      ) : (
        <SimpleTable
          columns={columns}
          data={rows}
          keyExtractor={(r) => r.id}
          onRowClick={(r) =>
            navigate(AppRoutes.ADMIN_ORDER_DISPUTE_DETAILS.replace(":id", r.id))
          }
          footer={
            pageCount > 1 ? (
              <div className="flex justify-center">
                <Pagination page={page} pageCount={pageCount} onPageChange={setPage} />
              </div>
            ) : undefined
          }
        />
      )}
    </div>
  );
}

export default AdminOrderDisputesPage;
